import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  BARCODE_FORMATS,
  DEFAULT_SHELF_LEVELS,
  LOCATION_TYPE_LABEL_HU,
  LocationType,
  MAX_SHELF_LEVELS,
  MIN_SHELF_LEVELS,
  formatBarcode,
} from '@persped/shared';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { usePrintLocationRange } from '../../api/queries';
import { ProblemAlert } from '../../components/ProblemAlert';

/**
 * Lokációcímke-TARTOMÁNY nyomtatása (2026-08-14-i CR #23).
 *
 * A LÉNYEG, ami megkülönbözteti a lokáció adatlapjáról indított
 * nyomtatástól: ez LÉTREHOZZA a hiányzó lokációkat. A fizikai munkafolyamat
 * ezt kívánja — ötven üres raklap áll a raktárban, és a dolgozó
 * felragasztja rájuk az RKL-0001…0050-et. Előbb kell a szám, aztán az
 * objektum, pontosan úgy, mint a mappacímkénél.
 *
 * A már létező lokációkat nem bántja, ezért egy megismételt nyomtatás
 * ártalmatlan — ami azért fontos, mert a nyomtatás kimenetele fizikailag nem
 * megfigyelhető, és az újrapróbálás normális.
 */
export function PrintRangeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const print = usePrintLocationRange();
  const [locationType, setLocationType] = useState<LocationType>(LocationType.PALLET);
  const [from, setFrom] = useState('1');
  const [to, setTo] = useState('10');
  const [namePrefix, setNamePrefix] = useState('');
  const [levelCount, setLevelCount] = useState(String(DEFAULT_SHELF_LEVELS));

  const fromNum = Number(from);
  const toNum = Number(to);
  const ervenyes =
    Number.isInteger(fromNum) &&
    Number.isInteger(toNum) &&
    fromNum >= 1 &&
    toNum >= fromNum &&
    toNum <= 9999 &&
    toNum - fromNum + 1 <= 200;

  const darab = ervenyes ? toNum - fromNum + 1 : 0;
  const elotag = BARCODE_FORMATS[locationType].prefix;

  const bezar = () => {
    print.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={bezar} fullWidth maxWidth="sm">
      <DialogTitle>Címketartomány nyomtatása</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {print.isError && <ProblemAlert error={print.error} />}

          {print.isSuccess ? (
            <Alert severity="success">
              <AlertTitle>Nyomtatásra küldve</AlertTitle>
              {print.data.createdBarcodes.length > 0 && (
                <Typography variant="body2">
                  Létrehozva: <strong>{print.data.createdBarcodes.length}</strong> új lokáció (
                  {print.data.createdBarcodes[0]} … {print.data.createdBarcodes.at(-1)}).
                </Typography>
              )}
              {print.data.existingBarcodes.length > 0 && (
                <Typography variant="body2">
                  Már létezett: {print.data.existingBarcodes.length} — ezeket nem módosítottuk.
                </Typography>
              )}
              <Typography variant="body2" sx={{ pt: 1 }}>
                <RouterLink to={`/cimkek/${print.data.batch.id}`}>Köteg megnyitása</RouterLink>
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="info">
                A hiányzó lokációk <strong>létrejönnek</strong>, hogy a címke felragasztható legyen
                a még nem regisztrált tárgyra. A már meglévőket nem nevezzük át és nem duplikáljuk
                — egy megismételt nyomtatás ártalmatlan.
              </Alert>

              <TextField
                select
                label="Típus"
                value={locationType}
                onChange={(e) => setLocationType(e.target.value as LocationType)}
              >
                {Object.values(LocationType).map((t) => (
                  <MenuItem key={t} value={t}>
                    {LOCATION_TYPE_LABEL_HU[t]} ({BARCODE_FORMATS[t].prefix}####)
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Ettől"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Eddig"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Stack>

              <TextField
                label="Név előtagja"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                placeholder={LOCATION_TYPE_LABEL_HU[locationType]}
                helperText={`A létrehozott lokációk neve: „előtag sorszám”. Üresen hagyva a típus neve.`}
              />

              {locationType === LocationType.SHELF && (
                <TextField
                  label="Szintek száma az új polcokon"
                  value={levelCount}
                  onChange={(e) => setLevelCount(e.target.value)}
                  helperText={`${MIN_SHELF_LEVELS}–${MAX_SHELF_LEVELS}. A meglévő polcokat nem érinti.`}
                />
              )}

              {ervenyes ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>{darab}</strong> címke: {formatBarcode(locationType, fromNum)} …{' '}
                  {formatBarcode(locationType, toNum)}
                </Typography>
              ) : (
                <Typography variant="body2" color="error">
                  Adj meg érvényes tartományt — legfeljebb 200 címke, {elotag}0001 és {elotag}9999
                  között.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={bezar}>{print.isSuccess ? 'Bezárás' : 'Mégse'}</Button>
        {!print.isSuccess && (
          <Button
            variant="contained"
            disabled={!ervenyes || print.isPending}
            onClick={() =>
              print.mutate({
                locationType,
                fromSequence: fromNum,
                toSequence: toNum,
                namePrefix: namePrefix.trim() || undefined,
                levelCount:
                  locationType === LocationType.SHELF ? Number(levelCount) : undefined,
              })
            }
          >
            Nyomtatás ({darab} db)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
